<template>
	<section
		class="bg-color-content-primary marketing-section"
		:class="{
			'bg-img-grid-circles': !appStore.isMobile
		}"
	>
		<div class="container px-100-smo">
			<div class="text-align-center-md display-flex flex-direction-column align-items-center gap-150-smo gap-300">
				<h1 class="heading-1 text-color-gold-50"
					>A more hands-on <br />
					Fundrise experience</h1
				>
				<div class="col-6-lg">
					<p class="text-color-white body-xl"
						>While most of our investors say they prefer to spend as little time managing their portfolio as
						possible, some want the ability to optimize their Fundrise holdings according to their own
						beliefs about the market. For those investors, we're introducing Fundrise Pro.
					</p>
				</div>
			</div>
			<div class="display-flex flex-direction-column mt-500 gap-1150-md gap-400-smo">
				<div
					v-for="feature in featureDetails"
					:key="feature.title"
					class="py-0-smo display-flex-md justify-content-center gap-600"
				>
					<div class="col-5-md pro-bt">
						<p class="overline text-color-white">{{ feature.title }}</p>
						<h2 class="heading-1 text-color-gold-50 mt-150 mt-50-smo">{{ feature.heading }}</h2>
						<p class="delta text-color-white" v-html="feature.description" />
					</div>
					<div class="pro-phone-video-container col-4-md mt-250-smo">
						<video
							class="pro-phone-video"
							muted
							loop
							autoplay="true"
							crossorigin="Anonymous"
							role="presentation"
							webkit-playsinline="true"
							playsinline="true"
							disableRemotePlayback="true"
							preload="auto"
							width="100%"
							:poster="feature.fallbackimage"
						>
							<source :src="feature.videoLink" type="video/mp4" />
						</video>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
import { appCloudfrontPath } from '@constants';
import { useAppStore } from '@stores/app';

const appStore = useAppStore();

const featureDetails: Array<{
	title: string;
	heading: string;
	description: string;
	videoLink: string;
	fallbackimage: string;
}> = [
	{
		title: 'Custom allocation',
		heading: 'Build your own custom investment plan',
		description:
			'With custom investment plans, Fundrise Pro members have the ability to control and shape their portfolio exactly how they want — picking the exact allocation across any number of our available funds. Members can always update their plan as their investment objectives or financial goals change, or easily switch between custom and Fundrise-directed plans.',
		videoLink: `${appCloudfrontPath}/video/Custom-Allocation.mp4`,
		fallbackimage: `${appCloudfrontPath}/images/marketing-site/custom-allocation-mobile.png`
	},
	{
		title: 'Direct investment',
		heading: 'Directly invest in specific funds',
		description:
			'With Fundrise Pro, in addition to members being able to create their own custom plan, we’re also making the ability to invest directly into specific funds easier than ever. Now members can browse our investments with ease, view relevant information, and place direct investments from the website, Android, or iOS apps.',
		videoLink: `${appCloudfrontPath}/video/Direct-Invest.mp4`,
		fallbackimage: `${appCloudfrontPath}/images/marketing-site/direct-invest-mobile.png`
	}
];
</script>
<style lang="scss" scoped>
@use '../../../styles/constants/_colors.scss' as *;
@use '../../../styles/utilities/respond-to.scss' as *;
@use 'sass:map';

.pro-bt {
	border-top: 1px solid map.get($brand-colors, gold-50);
	padding-top: 3rem;
	margin-top: 7rem;
	@include respond-to(smo) {
		padding-top: 4rem;
		margin-top: 0;
	}
}
.marketing-section {
	padding-top: 11.5rem;
	padding-bottom: 11.5rem;
	@include respond-to(smo) {
		padding-top: 4rem;
		padding-bottom: 4rem;
	}
	@include respond-to(md) {
		.gap-1150-md {
			gap: 11.5rem;
		}
	}
}
.pro-phone-video-container {
	line-height: 0;
}
.pro-phone-video {
	&:focus {
		outline: none;
	}
	@include respond-to(md) {
		height: fit-content;
		max-height: 80vh;
		min-height: 600px;
	}
}
</style>
